import { z } from 'zod';

import type {
  CreateYardLocationRequest,
  CreateYardLocationResponse,
  GetYardLocationRequest,
  GetYardLocationResponse,
  ListYardLocationsByLocationRequest,
  ListYardLocationsByLocationResponse,
  UpdateYardLocationRequest,
  UpdateYardLocationResponse,
  YardLocationDto,
} from './yard-location.dto.js';

const timestampSchema = z.iso.datetime();

export const YardLocationSchema: z.ZodType<YardLocationDto> =
  z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    name: z.string().trim().min(1),
    code: z.string().trim().min(1).optional(),
    description: z.string().trim().min(1).nullable().optional(),
    displayOrder: z.number().int().min(0),
    isActive: z.boolean(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  });

export const CreateYardLocationRequestSchema: z.ZodType<CreateYardLocationRequest> = z.object({
  companyId: z.uuid(),
  storeId: z.uuid(),
  locationId: z.uuid(),
  name: z.string().trim().min(1),
  code: z.string().trim().min(1).optional(),
  description: z.string().trim().min(1).nullable().optional(),
  displayOrder: z.number().int().min(0).optional(),
  isActive: z.boolean().optional(),
});

export const CreateYardLocationResponseSchema: z.ZodType<CreateYardLocationResponse> = z.object({
  yardLocation: YardLocationSchema,
});

export const UpdateYardLocationRequestSchema: z.ZodType<UpdateYardLocationRequest> = z
  .object({
    companyId: z.uuid(),
    yardLocationId: z.uuid(),
    name: z.string().trim().min(1).optional(),
    code: z.string().trim().min(1).optional(),
    description: z.string().trim().min(1).nullable().optional(),
    displayOrder: z.number().int().min(0).optional(),
    isActive: z.boolean().optional(),
  })
  .refine(
    (value) =>
      value.name !== undefined
      || value.code !== undefined
      || value.description !== undefined
      || value.displayOrder !== undefined
      || value.isActive !== undefined,
    { message: 'At least one field must be provided to update yard location' },
  );

export const UpdateYardLocationResponseSchema: z.ZodType<UpdateYardLocationResponse> = z.object({
  yardLocation: YardLocationSchema,
});

export const GetYardLocationRequestSchema: z.ZodType<GetYardLocationRequest> = z.object({
  companyId: z.uuid(),
  yardLocationId: z.uuid(),
});

export const GetYardLocationResponseSchema: z.ZodType<GetYardLocationResponse> = z.object({
  yardLocation: YardLocationSchema,
});

export const ListYardLocationsByLocationRequestSchema: z.ZodType<ListYardLocationsByLocationRequest> =
  z.object({
    companyId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    page: z.number().int().min(1).optional(),
    pageSize: z.number().int().min(1).max(200).optional(),
    includeInactive: z.boolean().optional(),
  });

export const ListYardLocationsByLocationResponseSchema: z.ZodType<ListYardLocationsByLocationResponse> =
  z.object({
    items: z.array(YardLocationSchema),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1).max(200),
    total: z.number().int().min(0),
  });
